
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ExportedAccountState struct
 */
class ExportedAccountState {
  /**
   * Initialize struct
   * @param {!number=} login
   * @param {!number=} balance
   * @param {!number=} credit
   * @param {!number=} equity
   * @param {!number=} margin
   * @param {!number=} leverage
   * @constructor
   */
  constructor (login = new UInt64(0, 0), balance = 0.0, credit = 0.0, equity = 0.0, margin = 0.0, leverage = 0) {
    this.Login = login
    this.Balance = balance
    this.Credit = credit
    this.Equity = equity
    this.Margin = margin
    this.Leverage = leverage
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ExportedAccountState}
   * @param {!ExportedAccountState} other Other struct
   * @returns {!ExportedAccountState} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.Balance != null) {
      this.Balance = other.Balance
    } else {
      this.Balance = null
    }
    if (other.Credit != null) {
      this.Credit = other.Credit
    } else {
      this.Credit = null
    }
    if (other.Equity != null) {
      this.Equity = other.Equity
    } else {
      this.Equity = null
    }
    if (other.Margin != null) {
      this.Margin = other.Margin
    } else {
      this.Margin = null
    }
    if (other.Leverage != null) {
      this.Leverage = other.Leverage
    } else {
      this.Leverage = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ExportedAccountState}
   * @returns {!ExportedAccountState} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ExportedAccountStateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ExportedAccountStateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ExportedAccountState}
   * @param {!ExportedAccountState} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ExportedAccountState)) {
      throw new TypeError('Instance of ExportedAccountState is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ExportedAccountState}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      Balance: ((this.Balance != null) ? this.Balance : null),
      Credit: ((this.Credit != null) ? this.Credit : null),
      Equity: ((this.Equity != null) ? this.Equity : null),
      Margin: ((this.Margin != null) ? this.Margin : null),
      Leverage: ((this.Leverage != null) ? this.Leverage : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ExportedAccountState.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ExportedAccountState} other Object value
   * @returns {!ExportedAccountState} Created struct
   */
  static fromObject (other) {
    return new ExportedAccountState().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ExportedAccountState}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ExportedAccountState.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ExportedAccountState}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 12053
  }
}

export { ExportedAccountState };

/**
 * Fast Binary Encoding ExportedAccountState field model
 */
class FieldModelExportedAccountState extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Balance = new fbe.FieldModelDouble(buffer, this.Login.fbeOffset + this.Login.fbeSize)
    this.Credit = new fbe.FieldModelDouble(buffer, this.Balance.fbeOffset + this.Balance.fbeSize)
    this.Equity = new fbe.FieldModelDouble(buffer, this.Credit.fbeOffset + this.Credit.fbeSize)
    this.Margin = new fbe.FieldModelDouble(buffer, this.Equity.fbeOffset + this.Equity.fbeSize)
    this.Leverage = new fbe.FieldModelUInt32(buffer, this.Margin.fbeOffset + this.Margin.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelExportedAccountState}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelExportedAccountState}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize + this.Balance.fbeSize + this.Credit.fbeSize + this.Equity.fbeSize + this.Margin.fbeSize + this.Leverage.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelExportedAccountState}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra + this.Balance.fbeExtra + this.Credit.fbeExtra + this.Equity.fbeExtra + this.Margin.fbeExtra + this.Leverage.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelExportedAccountState}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelExportedAccountState.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelExportedAccountState}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 12053
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelExportedAccountState}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelExportedAccountState}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Balance.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Balance.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Balance.fbeSize

    if ((fbeCurrentSize + this.Credit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Credit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Credit.fbeSize

    if ((fbeCurrentSize + this.Equity.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Equity.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Equity.fbeSize

    if ((fbeCurrentSize + this.Margin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Margin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.Leverage.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Leverage.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Leverage.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelExportedAccountState}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelExportedAccountState}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelExportedAccountState}
   * @param {!ExportedAccountState} fbeValue Default value, defaults is new ExportedAccountState()
   * @returns {!ExportedAccountState} ExportedAccountState value
   */
  get (fbeValue = new ExportedAccountState()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelExportedAccountState}
   * @param {!ExportedAccountState} fbeValue ExportedAccountState value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Balance.fbeSize) <= fbeStructSize) {
      fbeValue.Balance = this.Balance.get()
    } else {
      fbeValue.Balance = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Balance.fbeSize

    if ((fbeCurrentSize + this.Credit.fbeSize) <= fbeStructSize) {
      fbeValue.Credit = this.Credit.get()
    } else {
      fbeValue.Credit = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Credit.fbeSize

    if ((fbeCurrentSize + this.Equity.fbeSize) <= fbeStructSize) {
      fbeValue.Equity = this.Equity.get()
    } else {
      fbeValue.Equity = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Equity.fbeSize

    if ((fbeCurrentSize + this.Margin.fbeSize) <= fbeStructSize) {
      fbeValue.Margin = this.Margin.get()
    } else {
      fbeValue.Margin = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.Leverage.fbeSize) <= fbeStructSize) {
      fbeValue.Leverage = this.Leverage.get()
    } else {
      fbeValue.Leverage = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Leverage.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelExportedAccountState}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelExportedAccountState}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelExportedAccountState}
   * @param {!ExportedAccountState} fbeValue ExportedAccountState value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelExportedAccountState}
   * @param {ExportedAccountState} fbeValue ExportedAccountState value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
    this.Balance.set(fbeValue.Balance)
    this.Credit.set(fbeValue.Credit)
    this.Equity.set(fbeValue.Equity)
    this.Margin.set(fbeValue.Margin)
    this.Leverage.set(fbeValue.Leverage)
  }
}

export { FieldModelExportedAccountState };

/**
 * Fast Binary Encoding ExportedAccountState model
 */
class ExportedAccountStateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelExportedAccountState(this.buffer, 4)
  }

  /**
   * Get the ExportedAccountState model
   * @this {!ExportedAccountStateModel}
   * @returns {!FieldModelExportedAccountState} model ExportedAccountState model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ExportedAccountStateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ExportedAccountStateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ExportedAccountStateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ExportedAccountStateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelExportedAccountState.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ExportedAccountStateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ExportedAccountStateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ExportedAccountStateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ExportedAccountStateModel}
   * @param {!ExportedAccountState} value ExportedAccountState value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ExportedAccountStateModel}
   * @param {!ExportedAccountState} value ExportedAccountState value, defaults is new ExportedAccountState()
   * @return {!object} Deserialized ExportedAccountState value and its size
   */
  deserialize (value = new ExportedAccountState()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ExportedAccountState(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ExportedAccountState(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ExportedAccountStateModel}
   * @param {!number} prev Previous ExportedAccountState model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ExportedAccountStateModel };
